import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ceuService from '../services/unifiedCEUService';
import securePhotoService from '../services/securePhotoService';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import CEUBadge from '../components/ui/CEUBadge';
import { CheckCircle, Edit, FileText, Upload, X } from 'lucide-react';

export default function ViewCEU() {
  const { ceuId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [ceu, setCeu] = useState(null);
  const [certificateUrl, setCertificateUrl] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    loadCEU();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ceuId]);

  const loadCEU = async () => {
    try {
      setLoading(true);
      const result = await ceuService.getCEU(user.userId, ceuId);
      
      if (result.success && result.data) {
        setCeu(result.data);
        
        // Generate pre-signed URL for certificate if it exists
        if (result.data.certificateUrl) {
          try {
            const signedUrl = await securePhotoService.getCertificateViewUrl(
              result.data.certificateUrl
            );
            
            if (signedUrl) {
              setCertificateUrl(signedUrl);
            } else {
              setCertificateUrl(result.data.certificateUrl);
            }
          } catch (err) {
            console.error('Error generating certificate URL:', err);
            setCertificateUrl(result.data.certificateUrl);
          }
        }
      } else {
        setError('CEU not found');
      }
    } catch (err) {
      console.error('Error loading CEU:', err);
      setError('Failed to load CEU');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (field) => {
    setUpdating(true);
    try {
      const updates = {
        [field]: !ceu[field]
      };
      
      const result = await ceuService.updateCEU(user.userId, ceuId, updates);
      if (result && !result.error) {
        setCeu({ ...ceu, [field]: !ceu[field] });
      }
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleCertificateUpload = async (file) => {
    setUploading(true);
    setError(null);
    
    try {
      // Validate file
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }
      
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only PDF, PNG, JPG, and JPEG files are allowed');
      }

      // Upload certificate
      const { s3PhotoService } = await import('../services/s3PhotoService');
      const uploadResult = await s3PhotoService.uploadCertificate(
        user.userId, 
        ceuId, 
        file
      );
      
      if (uploadResult.success) {
        // Update CEU record with certificate URL
        const updateResult = await ceuService.updateCEU(user.userId, ceuId, {
          certificateUrl: uploadResult.certificateUrl
        });
        
        if (updateResult && !updateResult.error) {
          // Update CEU state
          const updatedCeu = { ...ceu, certificateUrl: uploadResult.certificateUrl };
          setCeu(updatedCeu);
          
          // Generate pre-signed URL for immediate preview
          try {
            const signedUrl = await securePhotoService.getCertificateViewUrl(
              uploadResult.certificateUrl
            );
            setCertificateUrl(signedUrl || uploadResult.certificateUrl);
          } catch (err) {
            console.warn('Could not generate signed URL, using direct URL:', err);
            setCertificateUrl(uploadResult.certificateUrl);
          }
          
          setShowUploadForm(false);
        } else {
          throw new Error('Failed to update CEU record');
        }
      } else {
        throw new Error(uploadResult.error || 'Failed to upload certificate');
      }
    } catch (err) {
      setError(err.message || 'Failed to upload certificate');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !ceu) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Alert variant="error">{error || 'CEU not found'}</Alert>
        <Button onClick={() => navigate('/my-ceus')} className="mt-4">
          Back to My CEUs
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Quick Actions Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Workshop Details</h1>
          <CEUBadge hours={ceu.ceuHours || ceu.ceuHours} />
        </div>
        
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/my-ceus')}
          >
            ← Back to My CEUs
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/edit-ceu/${ceuId}`)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Workshop
          </Button>
          <Button
            variant={ceu.ridReceived ? "success" : "outline"}
            onClick={() => toggleStatus('ridReceived')}
            disabled={updating}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {ceu.ridReceived ? 'RID Received ✓' : 'Mark RID Received'}
          </Button>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {ceu.title}
            </h2>

            <div className="space-y-4">
              <DetailRow label="Instructor" value={ceu.instructor} />
              <DetailRow label="Provider" value={ceu.provider} />
              <DetailRow label="Organization" value={ceu.organization} />
              <DetailRow label="Date" value={ceu.date} />
              <DetailRow label="Category" value={ceu.category} />
              {ceu.subCategory && ceu.subCategory.length > 0 && (
                <DetailRow 
                  label="Subcategory" 
                  value={Array.isArray(ceu.subCategory) ? ceu.subCategory.join(', ') : ceu.subCategory} 
                />
              )}
              <DetailRow label="CEUs" value={ceu.ceuHours || ceu.ceuHours} />
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Description
            </h2>
            <p className="text-gray-700 whitespace-pre-wrap">
              {ceu.description}
            </p>
          </Card>
        </div>

        {/* Right Column - Certificate */}
        <div className="lg:col-span-1">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Certificate
              </h2>
              {!certificateUrl && !ceu.certificateUrl && !showUploadForm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowUploadForm(true)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              )}
            </div>

            {showUploadForm && (
              <div className="mb-4">
                <CertificateUploadForm
                  onUpload={handleCertificateUpload}
                  onCancel={() => setShowUploadForm(false)}
                  uploading={uploading}
                />
              </div>
            )}

            {certificateUrl ? (
              <div className="space-y-3">
                {ceu.certificateUrl?.toLowerCase().includes('.pdf') ? (
                  // PDF Preview
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <object
                      data={`${certificateUrl}#toolbar=0&navpanes=0&scrollbar=0&view=Fit&page=1`}
                      type="application/pdf"
                      className="w-full h-[250px]"
                    >
                      <div className="p-8 text-center bg-gray-50">
                        <FileText className="w-16 h-16 mx-auto mb-3 text-primary" />
                        <p className="text-sm text-gray-600">PDF Preview</p>
                      </div>
                    </object>
                  </div>
                ) : (
                  // Image Preview
                  <img 
                    src={certificateUrl} 
                    alt={`Certificate for ${ceu?.title || 'workshop'}`}
                    className="w-full rounded-lg border border-gray-200"
                  />
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => window.open(certificateUrl, '_blank')}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Full Certificate
                </Button>
              </div>
            ) : ceu.certificateUrl ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Loading certificate...</p>
              </div>
            ) : !showUploadForm ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="mb-3">No certificate uploaded</p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowUploadForm(true)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Certificate
                </Button>
              </div>
            ) : null}
          </Card>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  
  return (
    <div className="flex border-b border-gray-100 pb-2">
      <dt className="font-medium text-gray-600 w-1/3">{label}:</dt>
      <dd className="text-gray-900 w-2/3">{value}</dd>
    </div>
  );
}

function CertificateUploadForm({ onUpload, onCancel, uploading }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={uploading}
        />
        
        {selectedFile ? (
          <div className="space-y-2">
            <FileText className="w-8 h-8 mx-auto text-green-600" />
            <p className="text-sm font-medium text-green-800">
              {selectedFile.name}
            </p>
            <p className="text-xs text-gray-500">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFile(null)}
              className="text-red-600 hover:text-red-700"
            >
              <X className="w-4 h-4 mr-1" />
              Remove
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-8 h-8 mx-auto text-gray-400" />
            <p className="text-sm text-gray-600">
              <span className="font-medium">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-500">
              PDF, PNG, JPG or JPEG (MAX. 10MB)
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!selectedFile || uploading}
          className="flex-1"
        >
          {uploading ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Certificate
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={uploading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
